import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const { handleGoogleLogin, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('login'); // 'login' | 'register' | 'forgot'
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα αποστολής email');
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login-email`,
        { email: form.email, password: form.password },
        { withCredentials: true }
      );
      localStorage.setItem('session_token', res.data.token);
      await refreshUser();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα σύνδεσης');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) {
      setError('Οι κωδικοί δεν ταιριάζουν');
      return;
    }
    if (form.password.length < 6) {
      setError('Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`,
        { name: form.name, email: form.email, password: form.password },
        { withCredentials: true }
      );
      localStorage.setItem('session_token', res.data.token);
      await refreshUser();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα εγγραφής');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="w-full max-w-sm">

        {/* Logo */}
        <div className="flex justify-center mb-10">
          <img src="/dimakas-logo.png" alt="DIMAKAS" style={{ maxWidth: '220px', height: 'auto' }} data-testid="login-logo" />
        </div>

        {/* Tab switcher */}
        <div className="flex mb-6 border-b border-white/10">
          {[['login', 'Σύνδεση'], ['register', 'Εγγραφή']].map(([id, label]) => (
            <button
              key={id}
              onClick={() => { setTab(id); setError(''); setForgotSent(false); }}
              className={`flex-1 py-2.5 text-sm font-light tracking-widest transition-all border-b-2 -mb-px ${
                tab === id ? 'border-white text-white' : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
              data-testid={`tab-${id}`}
            >
              {label.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-light" data-testid="auth-error">
            {error}
          </div>
        )}

        {/* LOGIN FORM */}
        {tab === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={set('email')}
              required
              className="w-full bg-white/5 border border-white/15 text-white placeholder:text-gray-600 px-4 py-3 text-sm font-light focus:outline-none focus:border-white/40 transition-colors"
              data-testid="email-input"
            />
            <input
              type="password"
              placeholder="Κωδικός"
              value={form.password}
              onChange={set('password')}
              required
              className="w-full bg-white/5 border border-white/15 text-white placeholder:text-gray-600 px-4 py-3 text-sm font-light focus:outline-none focus:border-white/40 transition-colors"
              data-testid="password-input"
            />
            <div className="text-right">
              <button
                type="button"
                onClick={() => setTab('forgot')}
                className="text-gray-500 text-xs font-light tracking-widest hover:text-gray-300 transition-colors"
                data-testid="forgot-password-link"
              >
                ΞΕΧΑΣΑ ΤΟΝ ΚΩΔΙΚΟ ΜΟΥ
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black py-3.5 text-sm font-light tracking-widest hover:bg-gray-100 transition-colors disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? 'ΣΥΝΔΕΣΗ...' : 'ΣΥΝΔΕΣΗ'}
            </button>
          </form>
        )}

        {/* REGISTER FORM */}
        {tab === 'register' && (
          <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
            <input
              type="text"
              placeholder="Ονοματεπώνυμο"
              value={form.name}
              onChange={set('name')}
              required
              className="w-full bg-white/5 border border-white/15 text-white placeholder:text-gray-600 px-4 py-3 text-sm font-light focus:outline-none focus:border-white/40 transition-colors"
              data-testid="name-input"
            />
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={set('email')}
              required
              className="w-full bg-white/5 border border-white/15 text-white placeholder:text-gray-600 px-4 py-3 text-sm font-light focus:outline-none focus:border-white/40 transition-colors"
              data-testid="register-email-input"
            />
            <input
              type="password"
              placeholder="Κωδικός (τουλάχιστον 6 χαρακτήρες)"
              value={form.password}
              onChange={set('password')}
              required
              className="w-full bg-white/5 border border-white/15 text-white placeholder:text-gray-600 px-4 py-3 text-sm font-light focus:outline-none focus:border-white/40 transition-colors"
              data-testid="register-password-input"
            />
            <input
              type="password"
              placeholder="Επιβεβαίωση Κωδικού"
              value={form.confirm}
              onChange={set('confirm')}
              required
              className="w-full bg-white/5 border border-white/15 text-white placeholder:text-gray-600 px-4 py-3 text-sm font-light focus:outline-none focus:border-white/40 transition-colors"
              data-testid="confirm-password-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black py-3.5 text-sm font-light tracking-widest hover:bg-gray-100 transition-colors disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {loading ? 'ΕΓΓΡΑΦΗ...' : 'ΕΓΓΡΑΦΗ'}
            </button>
          </form>
        )}

        {/* FORGOT PASSWORD FORM */}
        {tab === 'forgot' && (
          <div data-testid="forgot-password-section">
            {forgotSent ? (
              <div className="text-center space-y-4">
                <div className="px-4 py-3 bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-light" data-testid="forgot-sent-message">
                  Αν το email υπάρχει, θα λάβετε οδηγίες επαναφοράς.
                </div>
                <button
                  onClick={() => { setTab('login'); setForgotSent(false); setError(''); }}
                  className="text-gray-500 text-xs font-light tracking-widest hover:text-gray-300 transition-colors"
                >
                  ΕΠΙΣΤΡΟΦΗ ΣΤΗ ΣΥΝΔΕΣΗ
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-password-form">
                <p className="text-gray-500 text-xs font-light tracking-wide leading-relaxed">
                  Εισάγετε το email σας και θα σας στείλουμε σύνδεσμο επαναφοράς κωδικού.
                </p>
                <input
                  type="email"
                  placeholder="Email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  className="w-full bg-white/5 border border-white/15 text-white placeholder:text-gray-600 px-4 py-3 text-sm font-light focus:outline-none focus:border-white/40 transition-colors"
                  data-testid="forgot-email-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-white text-black py-3.5 text-sm font-light tracking-widest hover:bg-gray-100 transition-colors disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'ΑΠΟΣΤΟΛΗ...' : 'ΑΠΟΣΤΟΛΗ ΣΥΝΔΕΣΜΟΥ'}
                </button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setTab('login'); setError(''); }}
                    className="text-gray-500 text-xs font-light tracking-widest hover:text-gray-300 transition-colors"
                  >
                    ΕΠΙΣΤΡΟΦΗ ΣΤΗ ΣΥΝΔΕΣΗ
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Divider + Google — only show on login/register tabs */}
        {tab !== 'forgot' && (
          <>
            <div className="flex items-center my-6">
              <div className="flex-1 border-t border-white/10" />
              <span className="px-4 text-gray-600 text-xs font-light tracking-widest">Η</span>
              <div className="flex-1 border-t border-white/10" />
            </div>

            {/* Google Sign-In */}
            <button
              onClick={handleGoogleLogin}
              data-testid="google-login-button"
              className="w-full bg-transparent border border-white/20 hover:border-white/40 text-white font-light py-3.5 px-6 transition-all flex items-center justify-center space-x-3 text-sm tracking-widest"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>ΣΥΝΔΕΣΗ ΜΕ GOOGLE</span>
            </button>
          </>
        )}

      </div>
    </div>
  );
}
